<template>
  <CodSheet v-model="isOpen">
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="flex min-h-12 shrink-0 items-center gap-2 px-2 pt-2">
        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          :class="SHEET_LEADING_CONTROL_CLASS"
          icon="i-lucide-x"
          :aria-label="t('general.close')"
          @click="isOpen = false"
        />

        <h2 class="flex-1 truncate text-panel-title">{{ t('connection.heading') }}</h2>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div class="stat-list mx-4 my-3">
          <div class="flex items-center border-b border-default px-4">
            <div
              class="connection"
              :class="{ error: connectionsStatus.internet === 'error' }"
            >
              <i class="fal fa-wifi"></i>
            </div>

            <div class="min-w-0 flex-1 py-2">
              <div class="font-semibold">{{ t('connection.internet') }}</div>
              <p v-if="connectionsStatus.internet === 'error'" class="text-body text-error">
                {{ t('connection.internet_offline_message') }}
              </p>
              <p v-else class="text-body text-success">
                {{ t('connection.internet_online_message') }}
              </p>
            </div>
          </div>

          <div
            v-if="showLocalConnection"
            class="flex items-center px-4"
          >
            <div
              class="connection"
              :class="{
                error: connectionsStatus.local === 'error',
                warning: connectionsStatus.local === 'warning'
              }"
            >
              <i class="fal fa-network-wired"></i>
            </div>

            <div class="min-w-0 flex-1 py-2">
              <div class="font-semibold">{{ t('connection.local') }}</div>
              <p v-if="connectionsStatus.local === 'error'" class="text-body text-error">
                {{ t('connection.local_offline_message') }}
              </p>
              <p v-else-if="connectionsStatus.local === 'warning'" class="text-body text-warning">
                {{ t('connection.local_warning_message') }}
              </p>
              <p v-else class="text-body text-success">
                {{ t('connection.local_online_message') }}
              </p>
            </div>
          </div>
        </div>

        <!-- iOS discovers the register via Bonjour; this manual address is the
             fallback for venues whose register does not advertise mDNS yet. -->
        <div v-if="isIOS && showLocalConnection" class="mx-4 my-3">
          <UFormField
            :label="t('connection.manual_server_address')"
            :help="t('connection.manual_server_address_hint')"
          >
            <UInput
              v-model="manualServerAddress"
              class="w-full"
              placeholder="192.168.1.50"
              @blur="saveManualServerAddress"
            >
              <template v-if="manualServerAddress" #trailing>
                <UButton
                  color="neutral"
                  variant="link"
                  icon="i-lucide-x"
                  :aria-label="t('general.cancel')"
                  @click="manualServerAddress = ''; saveManualServerAddress()"
                />
              </template>
            </UInput>
          </UFormField>
        </div>

        <template v-if="showLocalConnection && store.localConnection.terminals.length > 0">
          <div class="mx-7 mt-4">
            <div>{{ t('general.terminal', 2) }}</div>
          </div>

          <div class="stat-list mx-4 my-3">
            <div
              v-for="(ter, index) in store.localConnection.terminals"
              :key="'trm' + index"
              class="flex items-center px-4"
              :class="{ 'border-b border-default': index !== store.localConnection.terminals.length - 1 }"
            >
              <div class="platform">
                <i v-if="!ter.terminal" class="fal fa-question-circle"></i>
                <i v-else-if="ter.terminal.platform === 'electron'" class="fal fa-desktop"></i>
                <i v-else class="fal fa-mobile"></i>
              </div>

              <div class="min-w-0 flex-1 py-2 text-warning">
                <div v-if="ter.terminal" class="font-semibold">{{ ter.terminal ? ter.terminal.name : '' }}</div>
                <p class="text-body">
                  {{ ter.address }}
                </p>
              </div>

              <UIcon v-if="ter.connected" name="i-lucide-check" class="size-7 shrink-0 text-success" />
              <UIcon v-else name="i-lucide-x" class="size-7 shrink-0 text-error" />
            </div>
          </div>
        </template>
      </div>

      <div class="flex shrink-0 justify-center p-2">
        <UButton
          class="w-62.5"
          size="lg"
          color="neutral"
          variant="outline"
          @click="isOpen = false"
        >
          {{ t('general.close') }}
        </UButton>
      </div>
    </div>
  </CodSheet>
</template>

<script setup lang="ts">
import { Ref, computed, inject, onMounted, onUnmounted, ref } from 'vue';
import { Terminal, appStorage, AppStorageKey, localNetworkService } from 'app-core';
import { Emitter } from '@/utilities/emitter';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/useAppStore';
import { CodSheet, SHEET_LEADING_CONTROL_CLASS } from 'app-components';

const { t } = useI18n()
const store = useAppStore()
const emitter = inject('emitter') as Emitter

const isOpen = ref(false)
const isWeb = computed(() => store.device?.platform === 'web')
const isIOS = computed(() => store.device?.platform === 'ios')
const terminals: Ref<Terminal[]> = ref([])
const manualServerAddress = ref('')

onMounted(async () => {
  emitter.on('showConnectionManagement', show)
})

onUnmounted(() => {
  emitter.off('showConnectionManagement', show)
})

const showLocalConnection = computed(() => !isWeb.value && !!store.terminal && terminals.value.some(t => t.isKitchenDisplaySystem() || t.isOrderingDevice()))
const hasConnectedTerminals = computed(() => store.localConnection.terminals.filter(t => t.connected).length > 1)
const connectionsStatus = computed(() => {
  return {
    internet: store.isOnline ? 'success' : 'error',
    local: !store.localConnection.connected ? 'error' : (store.localConnection.isServer && !hasConnectedTerminals.value ? 'warning' : 'success')
  }
})

async function show() {
  terminals.value = Terminal.all()
  isOpen.value = true

  if (isIOS.value) {
    manualServerAddress.value = await appStorage.local().getItem<string>(AppStorageKey.lanServerAddress) || ''
  }
}

async function saveManualServerAddress() {
  const address = manualServerAddress.value?.trim() || ''
  const stored = await appStorage.local().getItem<string>(AppStorageKey.lanServerAddress) || ''
  if (address === stored) return

  if (address) {
    await appStorage.local().setItem(AppStorageKey.lanServerAddress, address)
  } else {
    await appStorage.local().deleteItem(AppStorageKey.lanServerAddress)
  }

  await localNetworkService.reload()
}
</script>

<style lang="scss" scoped>
// One 44px circle geometry for both the status and the platform icons;
// the status circle adds its solid color fill.
.connection,
.platform {
  width: 44px;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: var(--type-screen-title);
  margin: 12px 20px 12px 4px;
  flex-shrink: 0;
}

.connection {
  background: var(--ui-success);
  color: white; // icon on solid color is always white (design §2)

  &.error {
    background: var(--ui-error);
    // Shared attention pulse (theme.css).
    animation: app-pulse-strong 1.2s ease-in-out infinite;
  }

  &.warning {
    background: var(--ui-warning);
  }
}
</style>
